import { Badge, I, type BadgeKind } from "../../ui";
import type { OmonimiSituation, ProspectDetail, ProspectListItem } from "../../api";
import { formatItalianDate, personName } from "../../format";
import { listAddress } from "./property-format";

export function buildProspectSubtitle(d: ProspectDetail): string {
  const parts: string[] = [];
  if (d.dataDecesso) parts.push(`decesso ${formatItalianDate(d.dataDecesso)}`);
  if (d.eta != null) parts.push(`${d.eta} anni`);
  if (d.dataNascita) parts.push(`nascita ${formatItalianDate(d.dataNascita)}`);
  else if (d.annoUsed != null) parts.push(`nascita stimata ${d.annoUsed}`);
  if (d.comune || d.luogo) parts.push(d.comune || d.luogo || "");
  if (d.provincia) parts.push(d.provincia);
  return parts.join(" · ");
}

export function formatDateTime(ts: number | null): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("it-IT");
}

interface ProspectOutcomeInput {
  status: string;
  numImmobili: number;
  omonimiCount: number;
  omonimiSituation?: OmonimiSituation;
  hasVisuraXml: boolean;
  hasImmobiliJson: boolean;
  matchedCf?: string | null;
  confirmedOmonimoId?: string | null;
}

export interface ProspectOutcome {
  kind: BadgeKind;
  label: string;
  title: string;
  detail: string;
  next: string;
}

export function prospectOutcome(p: ProspectOutcomeInput): ProspectOutcome {
  switch (p.status) {
    case "visura_pdf":
      return { kind: "warn", label: "Dati da recuperare", title: "Dati catastali da recuperare", detail: "SISTER ha restituito la visura, ma i dati non sono stati letti.", next: "Segnala la scheda all'assistenza: il recupero è tecnico." };
    case "visura_ok":
      if (p.numImmobili > 0) {
        return {
          kind: "info",
          label: `${p.numImmobili} immobili`,
          title: "Immobili trovati",
          detail: "La visura è stata letta: gli immobili sono nella scheda.",
          next: "Controlla gli immobili e invia a Herobot.",
        };
      }
      if (p.hasVisuraXml || p.hasImmobiliJson) {
        return {
          kind: "warn",
          label: "visura senza immobili",
          title: "Visura senza immobili",
          detail: "La visura è arrivata ma non contiene immobili.",
          next: "Se pensi che manchi qualcosa usa «Recupera dati visura», altrimenti scarta la scheda.",
        };
      }
      return {
        kind: "warn",
        label: "visura OK",
        title: "Visura da leggere",
        detail: "La ricerca è chiusa ma gli immobili non sono ancora stati letti.",
        next: "Premi «Recupera dati visura» in cima alla lista.",
      };
    case "omonimi_found":
      if (p.matchedCf || p.confirmedOmonimoId) {
        return {
          kind: "info",
          label: "visura in coda",
          title: "Omonimo confermato",
          detail: "Hai scelto la persona: l'estensione rifà la ricerca su SISTER e scarica la visura.",
          next: "Non serve altro: la scheda si aggiorna quando l'estensione ha finito.",
        };
      }
      if (p.omonimiSituation === "no_compatible") return {kind: "warn", label: "Nessun omonimo compatibile", title: "Nessun omonimo corrisponde", detail: "Gli omonimi trovati hanno date o luoghi di nascita diversi da quelli del necrologio.", next: "Controlla nome e data di nascita nel necrologio; se non riconosci nessuno, premi «Nessuno è la persona giusta»."};
      if (p.omonimiSituation === "clear_single") return {kind: "info", label: "Candidato consigliato", title: "Un omonimo corrisponde al necrologio", detail: "Un omonimo ha dati di nascita uguali o molto vicini a quelli del necrologio.", next: "Controlla l'omonimo consigliato e premi Conferma: l'estensione scarica i suoi immobili."};
      if (p.omonimiSituation === "incompatible_single") {
        return {
          kind: "warn",
          label: "omonimo incompatibile",
          title: "Unico omonimo ma dati non tornano",
          detail:
            "SISTER ha trovato una sola persona con questo nome, ma anno o luogo di nascita non combaciano col necrologio.",
          next: "Se non sei sicuro, premi «Nessuno è la persona giusta». Conferma solo se riconosci la persona.",
        };
      }
      if ((p.omonimiCount || 0) <= 1) {
        return {
          kind: "accent",
          label: "1 omonimo",
          title: "Omonimo da verificare",
          detail: "SISTER ha restituito un solo candidato; l'estensione non l'ha confermato in automatico.",
          next: "Confronta data e luogo di nascita, poi premi Conferma oppure «Nessuno è la persona giusta».",
        };
      }
      return {
        kind: "accent",
        label: `${p.omonimiCount || "?"} omonimi`,
        title: "Più persone con lo stesso nome",
        detail: "SISTER ha restituito più omonimi; l'estensione non ha scelto in automatico.",
        next: "Scegli l'omonimo giusto e premi Conferma.",
      };
    case "no_match":
      return {
        kind: "muted",
        label: "nessuna corrispondenza",
        title: "Nessuna corrispondenza",
        detail: "La ricerca non ha trovato un soggetto compatibile.",
        next: "Controlla nome e data di nascita nel necrologio: se sono giusti, non risultano immobili a suo nome e puoi scartare la scheda.",
      };
    case "captcha_pending":
      return {
        kind: "warn",
        label: "codice di sicurezza in attesa",
        title: "Captcha non risolto",
        detail: "SISTER ha chiesto il codice di sicurezza e non è ancora stato letto.",
        next: "Rimetti la ricerca in coda da Necrologi con «Riprova le non riuscite»: di solito al secondo tentativo va.",
      };
    case "captcha_failed":
      return {
        kind: "danger",
        label: "codice di sicurezza fallito",
        title: "Captcha non letto",
        detail: "Il codice di sicurezza non è stato riconosciuto.",
        next: "Rimetti la ricerca in coda da Necrologi con «Riprova le non riuscite».",
      };
    case "tipo_visura_bounce":
      return {
        kind: "warn",
        label: "scelta tipo visura",
        title: "Blocco sulla scelta tipo visura",
        detail: "SISTER è rimasto sulla schermata del tipo di documento.",
        next: "Rimetti la ricerca in coda da Necrologi con «Riprova le non riuscite».",
      };
    case "too_many_results":
      return {
        kind: "warn",
        label: "troppi omonimi",
        title: "Oltre il limite SISTER (400 risultati)",
        detail:
          "Cognome molto comune: SISTER non mostra la lista sopra i 400 risultati, anche filtrando per anno di nascita.",
        next: "Serve un dato in più (provincia o data di nascita completa) per restringere; poi rimetti in coda.",
      };
    case "error":
      return {
        kind: "danger",
        label: "errore",
        title: "Errore SISTER",
        detail: "La ricerca non si è conclusa correttamente.",
        next: "Rimetti la ricerca in coda da Necrologi con «Riprova le non riuscite»; se si ripete, avvisa l'assistenza.",
      };
    case "pending":
      return {
        kind: "warn",
        label: "in coda",
        title: "In attesa dell'estensione",
        detail: "La ricerca è in coda e non ha ancora un esito.",
        next: "Nessuna azione: l'estensione la cerca appena è accesa.",
      };
    default:
      return {
        kind: "muted",
        label: p.status || "sconosciuto",
        title: "Stato non classificato",
        detail: `Ultimo stato: ${p.status || "sconosciuto"}.`,
        next: "Apri il dettaglio.",
      };
  }
}

export interface ProspectRowActions {
  busy: boolean;
  onToggleWorked: (p: ProspectListItem) => void;
  onDiscard: (p: ProspectListItem) => void;
  onRestore: (p: ProspectListItem) => void;
}

export function ProspectRow({
  p,
  selected,
  onClick,
  actions,
  exportSelection,
}: {
  p: ProspectListItem;
  selected: boolean;
  onClick: () => void;
  actions: ProspectRowActions;
  exportSelection?: { checked: boolean; disabled: boolean; onChange: (checked: boolean) => void };
}) {
  const outcome = prospectOutcome(p);
  const name = personName(p.nome);
  const worked = p.workedAt != null;
  const address = p.primoImmobile ? listAddress(p.primoImmobile) : null;
  const otherImmobili = p.numImmobili - 1;
  const workedTitle = worked
    ? `Lavorata il ${new Date(p.workedAt!).toLocaleDateString("it-IT")}${p.workedBy ? ` da ${p.workedBy}` : ""}. Clic per rimetterla da lavorare.`
    : "Segna come già lavorata";
  return (
    <tr
      className={`clickable-row prospect-row${worked ? " is-worked" : ""}${selected ? " is-selected" : ""}${exportSelection ? " has-export-selection" : ""}`}
      tabIndex={0}
      aria-label={`Apri la scheda di ${name}`}
      onClick={onClick}
      onKeyDown={(e) => {
        // I pulsanti della riga gestiscono da soli Invio e spazio.
        if (e.target !== e.currentTarget) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      {exportSelection && <td className="prospect-export-cell">
        <input type="checkbox" checked={exportSelection.checked} disabled={exportSelection.disabled}
          aria-label={`Seleziona ${name} per Excel`} title="Seleziona per l'esportazione Excel"
          onClick={event => event.stopPropagation()} onChange={event => exportSelection.onChange(event.target.checked)} />
      </td>}
      <td className="prospect-check-cell">
        <button
          type="button"
          className="worked-toggle"
          aria-pressed={worked}
          aria-label={worked ? `${name}: già lavorata. Rimetti da lavorare` : `Segna ${name} come già lavorata`}
          title={workedTitle}
          disabled={actions.busy}
          onClick={(e) => {
            e.stopPropagation();
            actions.onToggleWorked(p);
          }}
        >
          {I.check}
        </button>
      </td>
      <td className="prospect-person-cell">
        <div className="prospect-person-name">
          {name}
          {p.dataDecesso && (
            <span className="mono muted" style={{ marginLeft: 6, fontSize: 11 }}>
              † {formatItalianDate(p.dataDecesso)}
            </span>
          )}
          {p.casaVuota && (
            <span style={{ marginLeft: 6 }}>
              <Badge kind="warn" title="Coniuge non citato nel necrologio: indicazione da verificare. Non identifica gli eredi né conferma che la casa sia vuota.">
                Coniuge non citato · Da verificare
              </Badge>
            </span>
          )}
          {p.vedovo && !p.casaVuota && (
            <span style={{ marginLeft: 6 }}>
              <Badge kind="info" title="Necrologio con ved./vedovo/vedova rilevato">ved.</Badge>
            </span>
          )}
          {p.discardedAt != null && (
            <span style={{ marginLeft: 6 }}>
              <Badge kind="muted">scartato</Badge>
            </span>
          )}
        </div>
        {address && (
          <div className="prospect-address" title={p.primoImmobile?.indirizzo ?? undefined}>
            <span className="prospect-address-icon" aria-hidden="true">{I.mappin}</span>
            <span className="prospect-address-text">{address}</span>
            {otherImmobili > 0 && <span className="prospect-address-more">+{otherImmobili} {otherImmobili === 1 ? "altro" : "altri"}</span>}
          </div>
        )}
        <div className="prospect-meta">
          {(p.comune || p.luogo) && <span>{p.comune || p.luogo}{p.provincia ? ` (${p.provincia})` : ""}</span>}
          <span>cercato il {new Date(p.searchedAt).toLocaleDateString("it-IT")}</span>
          {p.matchedCf && <span className="mono">CF {p.matchedCf}</span>}
        </div>
        {p.impresaFunebre && (
          <div className="muted" style={{ marginTop: 2, fontSize: 11 }}>
            {p.impresaFunebre}
          </div>
        )}
      </td>
      <td className="prospect-outcome-cell">
        <Badge kind={outcome.kind}>{outcome.label}</Badge>
      </td>
      <td className="prospect-next-cell">
        {worked && (
          <div className="prospect-worked-note">
            Già lavorata il {new Date(p.workedAt!).toLocaleDateString("it-IT")}
          </div>
        )}
        <div className={worked ? "prospect-next-muted" : undefined}>{outcome.next}</div>
      </td>
      <td className="prospect-immobili-cell">
        {p.numImmobili > 0 ? p.numImmobili.toLocaleString("it-IT") : p.status === "visura_ok" ? "0" : "—"}
      </td>
      <td className="prospect-actions-cell">
        {p.discardedAt != null ? (
          <button
            type="button"
            className="btn sm ghost"
            disabled={actions.busy}
            title="Rimetti la scheda nella lista di lavoro"
            onClick={(e) => {
              e.stopPropagation();
              actions.onRestore(p);
            }}
          >
            Rimetti
          </button>
        ) : (
          <button
            type="button"
            className="btn sm ghost"
            disabled={actions.busy}
            title="Togli la scheda dalla lista di lavoro. La ricerca SISTER resta com'è."
            aria-label={`Scarta ${name}`}
            onClick={(e) => {
              e.stopPropagation();
              actions.onDiscard(p);
            }}
          >
            Scarta
          </button>
        )}
      </td>
    </tr>
  );
}
